import { useEffect, type ReactNode } from 'react'
import Column from '@app/components/layout/Column'
import { match } from 'ts-pattern'
import { useQuery } from '@based/sdk/react'
import { MunicipalityViewModel } from 'functions/app/geography/view/municipality'
import { municipalityTabs } from '@app/pages/discover/regions/constants'
import { Initiatives } from '../regionTabs/tabContent/Initiatives'
import { Thread } from '@app/components/blocks/thread/Thread'
import { KnowledgeHubModulesSection } from '@app/components/blocks/KnowledgeHubModulesSection'
import { RegionalServiceProvidersGrid } from '../regionTabs/tabContent/RegionalServiceProvidersGrid'
import { ServiceProviderListRegionalModel } from 'functions/app/service-provider/list-regional'
import { MunicipalityActueelTab } from './MunicipalityActueelTab'
import { Locations } from '../regionTabs/tabContent/Locations'
import { StepByStep } from '../regionTabs/tabContent/StepByStep'
import { useMunicipalityLayoutTabAvailability } from './useMunicipalityLayoutTabAvailability'
import type { MunicipalityFeatures } from '@schema/featureToggle'
import { isFeatureEnabled } from '@app/util/featureToggle'
import { navigate } from 'wouter/use-browser-location'
import { pages } from '@app/util/links'

export const MunicipalityPageTabContent = ({
  municipality,
  municipalitySlug,
  tab,
}: {
  municipality: MunicipalityViewModel
  municipalitySlug: string
  tab: MunicipalityFeatures
}) => {
  const { isTabAvailable } = useMunicipalityLayoutTabAvailability(municipality)

  useEffect(() => {
    if (tab === municipalityTabs.overview) {
      return
    }
    if (!isTabAvailable(tab)) {
      navigate(pages.municipalityPageNew(municipalitySlug))
    }
  }, [tab, isTabAvailable, municipalitySlug])

  const expertsFeatureEnabled = isFeatureEnabled(
    municipality.featureToggles,
    municipalityTabs.experts,
  )
  const regionalExpertsListQuery = useQuery(
    tab === municipalityTabs.experts && expertsFeatureEnabled
      ? 'list-regional-service-providers'
      : null,
    { municipalityId: municipality.id },
  )
  const regionalExperts: ServiceProviderListRegionalModel[] =
    regionalExpertsListQuery.data !== undefined
      ? (regionalExpertsListQuery.data as ServiceProviderListRegionalModel[])
      : []

  if (tab === municipalityTabs.overview) {
    return null
  }

  return (
    <Column alignItems="stretch" gap="2rem">
      {match(tab)
        .with(municipalityTabs.current, () => (
          <MunicipalityActueelTab
            key="current"
            municipality={municipality}
            municipalitySlug={municipalitySlug}
          />
        ))
        .with(municipalityTabs.discussion, () => (
          <Thread key="thread" threadId={municipality.thread?.id} />
        ))
        .with(municipalityTabs.initiatives, () => (
          <Initiatives key="initiatives" entity={municipality} />
        ))
        .with(municipalityTabs.locations, () => (
          <Locations key="locations" entity={municipality} />
        ))
        .with(municipalityTabs.stepByStep, () => (
          <StepByStep key="stepByStep" region={municipality} />
        ))
        .with(municipalityTabs.knowledgeHub, () => (
          <KnowledgeHubModulesSection
            key="knowledgeHub"
            modules={municipality.knowledgeHubModules ?? []}
            getModuleLink={(slug) =>
              pages.municipalityKnowledgeHubModule(municipalitySlug, slug)
            }
          />
        ))
        .with(municipalityTabs.experts, () => (
          <RegionalServiceProvidersGrid
            key="experts"
            serviceProviders={regionalExperts}
          />
        ))
        .with(municipalityTabs.subsidies, (): ReactNode => null)
        .exhaustive()}
    </Column>
  )
}
